import React, { useState, useEffect, useMemo, useCallback } from "react";
import { format, parseISO, addDays, isValid } from "date-fns";
import FormularioVenta from "./FormularioVenta";
import ListaVentas from "./ListaVentas";
import ModalPassword from "./ModalPassword";
import ResumenDia from "./ResumenDia";
import VentasDesgloseModal from "./VentasDesgloseModal";
import EmbarcacionesSection from "./EmbarcacionesSection";
import { registrosApi } from "../../api/registrosApi";
import { Button, Input, Card } from "./UIElements";
import { es } from "date-fns/locale";
import { motion } from "framer-motion";
import * as XLSX from "xlsx-js-style";
import {
  agruparVentasPorMedio,
  calcularTotalesFinancieros
} from "../../utils/pagos";

const formatDateUTC = (dateString) => {
  const date = parseISO(dateString);
  return format(date, "d MMMM yyyy", { locale: es });
};

const generarIdempotencyKey = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const randomBytes = new Uint8Array(16);
    crypto.getRandomValues(randomBytes);
    return `fallback-${Array.from(randomBytes, (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
  }

  return `fallback-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const intentarParseJSON = (valor) => {
  if (valor == null) return null;
  if (typeof valor !== 'string') return valor;

  const texto = valor.trim();
  if (!texto) return null;

  try {
    return JSON.parse(texto);
  } catch (error) {
    return texto;
  }
};

const registrarFecha = (set, valor) => {
  if (valor == null) return;

  const agregar = (fecha) => {
    if (fecha && isValid(fecha)) {
      set.add(format(fecha, 'yyyy-MM-dd'));
    }
  };

  if (valor instanceof Date) {
    agregar(valor);
    return;
  }

  if (typeof valor === 'number') {
    const fecha = new Date(valor);
    agregar(fecha);
    return;
  }

  if (typeof valor === 'string') {
    const texto = valor.trim();
    if (!texto) return;

    const coincidencias = texto.match(/\d{4}-\d{2}-\d{2}/g);
    if (coincidencias) {
      coincidencias.forEach((match) => {
        const fecha = new Date(`${match}T00:00:00`);
        agregar(fecha);
      });
      if (coincidencias.length > 0) return;
    }

    const parsedIso = parseISO(texto);
    if (isValid(parsedIso)) {
      agregar(parsedIso);
      return;
    }

    const generica = new Date(texto);
    if (isValid(generica)) {
      agregar(generica);
      return;
    }

    texto
      .split(/[;,\n]+/)
      .map((parte) => parte.trim())
      .filter(Boolean)
      .forEach((parte) => registrarFecha(set, parte));
    return;
  }

  if (Array.isArray(valor)) {
    valor.forEach((item) => registrarFecha(set, item));
    return;
  }

  if (typeof valor === 'object') {
    Object.values(valor).forEach((item) => registrarFecha(set, item));
  }
};

const obtenerDiasVenta = (registro = {}) => {
  const dias = new Set();

  const detalleNormalizado = intentarParseJSON(
    registro.detalle ?? registro.detalles ?? registro.diasDetalle ?? null
  );

  registrarFecha(dias, detalleNormalizado);

  const fechaBaseCruda =
    registro.fecha ?? registro.createdAt ?? registro.fecha_emision ?? null;

  if (dias.size === 0 && fechaBaseCruda) {
    const fechaBase = new Date(fechaBaseCruda);
    if (isValid(fechaBase)) {
      const diasEstadia = Math.max(
        1,
        Number(registro.diasEstadia ?? registro.dias_estadia ?? 1) || 1
      );
      for (let i = 0; i < diasEstadia; i += 1) {
        const diaActual = addDays(fechaBase, i);
        if (isValid(diaActual)) {
          dias.add(format(diaActual, 'yyyy-MM-dd'));
        }
      }
    }
  }

  if (dias.size === 0 && registro.validoHasta) {
    const validoHasta = new Date(registro.validoHasta);
    if (isValid(validoHasta)) {
      dias.add(format(validoHasta, 'yyyy-MM-dd'));
    }
  }

  if (dias.size === 0) {
    dias.add(format(new Date(), 'yyyy-MM-dd'));
  }

  return dias;
};

const RegistroIngresos = () => {
  const [fecha, setFecha] = useState(new Date());
  const [embarcacionesIngresadas, setEmbarcacionesIngresadas] = useState(null);
  const [cargandoEmbarcaciones, setCargandoEmbarcaciones] = useState(false);
  const [ventasEntradas, setVentasEntradas] = useState([]);
  const [nuevaVenta, setNuevaVenta] = useState({
    cantidad: "0",
    medio_pago_id_2: null,
    monto_medio_pago_1: "",
    monto_medio_pago_2: "",
    diasEstadia: "1",
    extras: [],
  });
  const [totalVentas, setTotalVentas] = useState(0);
  const [totalClientes, setTotalClientes] = useState(0);
  const [totalVentasAnuladas, setTotalVentasAnuladas] = useState(0);
  const [totalClientesAnulados, setTotalClientesAnulados] = useState(0);
  const [valorEntrada, setValorEntrada] = useState(0);
  const [resumenDia, setResumenDia] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [filtroUsuario, setFiltroUsuario] = useState(null);
  const [medioPago, setMedioPago] = useState("efectivo");
  const [mediosDePago, setMediosDePago] = useState([]);
  const [extras, setExtras] = useState([]);
  const [ventasConMediosDePago, setVentasConMediosDePago] = useState([]);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState("");
  const [ventaPendiente, setVentaPendiente] = useState(null);
  const [enviandoVenta, setEnviandoVenta] = useState(false);
  const [modoPantallaCompleta, setModoPantallaCompleta] = useState(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem("modoPantallaCompletaRegistros");
    return stored !== null ? stored === "true" : true;
  });
  const [modoPasoAPaso, setModoPasoAPaso] = useState(false);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [usuarioSeleccionadoDesglose, setUsuarioSeleccionadoDesglose] =
    useState(null);
  const [ventasUsuarioMedioPago, setVentasUsuarioMedioPago] = useState({
    detalle: [],
    totales: {
      entradas: 0,
      montoTotal: 0,
      total: 0,
      dias: 0,
      diasAnulados: 0,
      extras: 0,
      extrasAnulados: 0,
      extrasCantidad: 0,
      extrasCantidadAnulados: 0,
      entradasAnuladas: 0,
      montoAnulado: 0
    }
  });
  const [formResetCounter, setFormResetCounter] = useState(0);

  const obtenerTotalClientesVenta = useCallback((venta = {}) => {
    const candidatos = [
      venta.totalClientes,
      venta.cantidad,
      venta.total_clientes,
      venta.cantidad_clientes,
    ];

    for (const valor of candidatos) {
      const numero = Number(valor);
      if (Number.isFinite(numero) && numero > 0) {
        return numero;
      }
    }

    return 0;
  }, []);

  const obtenerMontoVenta = useCallback((venta = {}) => {
    const candidatos = [
      venta.totalVentas,
      venta.total,
      venta.monto,
      venta.valor,
    ];

    for (const valor of candidatos) {
      const numero = Number(valor);
      if (Number.isFinite(numero)) {
        return numero;
      }
    }

    return 0;
  }, []);

  const resumenExtras = useMemo(() => {
    const activos = new Map();
    const anulados = new Map();

    const acumularExtra = (mapa, extra) => {
      if (!extra) return;
      const clave =
        extra?.extra?.id ??
        extra?.extra_id ??
        extra?.id ??
        extra?.extra?.nombre ??
        extra?.nombre;

      if (clave === undefined || clave === null) return;

      const nombre = extra?.extra?.nombre || extra?.nombre || `Extra ${clave}`;
      const cantidadNumero = Number(extra?.cantidad || 0);
      const cantidad = Number.isFinite(cantidadNumero) && cantidadNumero > 0 ? cantidadNumero : 0;
      const valorUnitario = Number(
        extra?.valor_unitario ?? extra?.valor ?? 0
      );
      const totalCalculado =
        extra?.total != null
          ? Number(extra.total)
          : valorUnitario * cantidad;
      const total = Number.isFinite(totalCalculado) ? totalCalculado : 0;

      const key = String(clave);
      const existente = mapa.get(key) || {
        id: typeof clave === 'number' ? clave : null,
        nombre,
        cantidad: 0,
        total: 0,
      };

      existente.cantidad += cantidad;
      existente.total += total;
      mapa.set(key, existente);
    };

    ventasEntradas.forEach((venta) => {
      const extrasVenta = Array.isArray(venta.extras) ? venta.extras : [];
      const mapaDestino = venta.anulado ? anulados : activos;
      extrasVenta.forEach((extra) => acumularExtra(mapaDestino, extra));
    });

    const mapToArray = (mapa) =>
      Array.from(mapa.values())
        .map((item) => ({
          ...item,
          cantidad: Number(item.cantidad || 0),
          total: Number(item.total.toFixed(2)),
        }))
        .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }));

    const calcularTotales = (mapa) => {
      let monto = 0;
      let cantidad = 0;
      mapa.forEach((item) => {
        monto += Number(item.total || 0);
        cantidad += Number(item.cantidad || 0);
      });
      return {
        monto: Number(monto.toFixed(2)),
        cantidad,
      };
    };

    const totalesActivos = calcularTotales(activos);
    const totalesAnulados = calcularTotales(anulados);

    return {
      activos: mapToArray(activos),
      anulados: mapToArray(anulados),
      totales: {
        montoActivos: totalesActivos.monto,
        montoAnulados: totalesAnulados.monto,
        cantidadActivos: totalesActivos.cantidad,
        cantidadAnulados: totalesAnulados.cantidad,
      },
    };
  }, [ventasEntradas]);

  const resumenDiasUnicos = useMemo(() => {
    const activos = new Set();
    const anulados = new Set();

    ventasEntradas.forEach((venta) => {
      const dias = Array.isArray(venta.diasDetalle) ? venta.diasDetalle : [];
      const destino = venta.anulado ? anulados : activos;
      dias.forEach((dia) => destino.add(dia));
    });

    return {
      activos: activos.size,
      anulados: anulados.size,
      detalleActivos: Array.from(activos).sort(),
      detalleAnulados: Array.from(anulados).sort(),
    };
  }, [ventasEntradas]);

  const totalesFinancieros = useMemo(
    () => calcularTotalesFinancieros(ventasEntradas),
    [ventasEntradas]
  );

  const desgloseMediosPago = useMemo(
    () => agruparVentasPorMedio(ventasEntradas),
    [ventasEntradas]
  );

  const fetchMediosDePago = async () => {
    try {
      const response = await registrosApi.getMediosDePago();
      setMediosDePago(response.data);
    } catch (error) {
      console.error("Error al obtener los medios de pago:", error);
    }
  };

  const fetchExtrasActivos = async () => {
    try {
      const response = await registrosApi.getExtrasActivos();
      const extrasDisponibles = response.data
        .map((extra) => ({
          ...extra,
          valor: Number(extra.valor),
        }))
        .sort((a, b) => a.nombre.localeCompare(b.nombre));
      setExtras(extrasDisponibles);
    } catch (error) {
      console.error("Error al obtener los extras:", error);
    }
  };

  const fetchRegistros = async (soloHoy = true) => {
    try {
      const response = await registrosApi.getRegistros(soloHoy);
      const registrosConMontos = response.data.map((registro) => {
        const extrasRegistro = (registro.extras || []).map((extra) => ({
          ...extra,
          cantidad: Number(extra.cantidad || 0),
          valor_unitario: Number(extra.valor_unitario || 0),
          total: Number(extra.total || 0),
          extra: extra.extra
            ? {
                ...extra.extra,
                valor: Number(extra.extra.valor || 0),
              }
            : null,
        }));

        const extrasSubtotal = extrasRegistro.reduce(
          (sum, extra) => sum + Number(extra.total || 0),
          0
        );

        const diasVenta = obtenerDiasVenta(registro);
        const diasDetalle = Array.from(diasVenta);

        const totalVentasNumero = obtenerMontoVenta(registro);
        const totalBaseCalculado = totalVentasNumero - extrasSubtotal;
        const totalClientesNumero = obtenerTotalClientesVenta(registro);

        return {
          ...registro,
          anulado: Boolean(registro.anulado),
          fechaAnulacion: registro.fechaAnulacion || registro.fecha_anulacion || null,
          monto_medio_pago_1: Number(registro.monto_medio_pago_1 || 0),
          monto_medio_pago_2: Number(registro.monto_medio_pago_2 || 0),
          diasEstadia: registro.diasEstadia || registro.dias_estadia || 1,
          validoHasta: registro.validoHasta || registro.valido_hasta || null,
          extras: extrasRegistro,
          extrasSubtotal: Number(extrasSubtotal.toFixed(2)),
          totalClientes: totalClientesNumero,
          cantidad: totalClientesNumero,
          totalVentas: totalVentasNumero,
          diasDetalle,
          diasUnicos: diasDetalle.length,
          totalBase: registro.totalBase != null
            ? Number(registro.totalBase)
            : Number(Math.max(totalBaseCalculado, 0).toFixed(2)),
        };
      });
      setVentasEntradas(registrosConMontos);
    } catch (error) {
      console.error("Error al obtener los registros:", error);
    }
  };

  const fetchUsuarios = async () => {
    try {
      const response = await registrosApi.getUsuarios();
      setUsuarios(response.data);
    } catch (error) {
      console.error("Error al obtener los usuarios:", error);
    }
  };

  const fetchVentasConMediosDePago = async () => {
    try {
      const response = await registrosApi.getVentasPorUsuario();
      setVentasConMediosDePago(response.data);
    } catch (error) {
      console.error("Error al obtener las ventas con medios de pago:", error);
    }
  };

  const fetchVentasPorUsuarioMedioPago = async (usuarioId) => {
    try {
      const response = await registrosApi.getVentasPorUsuarioMedioPago(
        usuarioId
      );
      const datosFormateados = response.data.map((item) => ({
        ...item,
        totalClientes: Number(item.totalClientes),
        totalVentas: Number(item.totalVentas),
      }));
      setVentasUsuarioMedioPago(datosFormateados);
      setModalAbierto(true);
    } catch (error) {
      console.error(
        "Error al obtener ventas por usuario y medio de pago:",
        error
      );
      alert(`Error al obtener el desglose: ${error.message}`);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchUsuarios();
      await fetchMediosDePago();
      await fetchExtrasActivos();
      await fetchRegistros(true);
      await fetchVentasConMediosDePago();
    };
    loadData();
  }, []);

  useEffect(() => {
    const fetchConfiguracion = async () => {
      try {
        const response = await registrosApi.getConfiguracion();
        setValorEntrada(response.data.valorEntrada);
      } catch (error) {
        console.error("Error al obtener la configuración:", error);
      }
    };
    fetchConfiguracion();
  }, []);

  useEffect(() => {
    const ventasActivas = ventasEntradas.filter((venta) => !venta.anulado);
    const ventasAnuladas = ventasEntradas.filter((venta) => venta.anulado);

    const total = ventasActivas.reduce(
      (sum, venta) => sum + obtenerMontoVenta(venta),
      0
    );
    const clientes = ventasActivas.reduce(
      (sum, venta) => sum + obtenerTotalClientesVenta(venta),
      0
    );
    const totalAnulado = ventasAnuladas.reduce(
      (sum, venta) => sum + obtenerMontoVenta(venta),
      0
    );
    const clientesAnulados = ventasAnuladas.reduce(
      (sum, venta) => sum + obtenerTotalClientesVenta(venta),
      0
    );
    setTotalVentas(total);
    setTotalClientes(clientes);
    setTotalVentasAnuladas(totalAnulado);
    setTotalClientesAnulados(clientesAnulados);
  }, [ventasEntradas, obtenerMontoVenta, obtenerTotalClientesVenta]);

  const handleObtenerEmbarcaciones = async () => {
    setCargandoEmbarcaciones(true);
    try {
      const response = await registrosApi.getHistoricoMovimientos();
      const movimientos = response.data;
      const embarcacionesHoy = movimientos.filter(
        (movimiento) =>
          format(new Date(movimiento.fechaHoraIngreso), "yyyy-MM-dd") ===
          format(new Date(), "yyyy-MM-dd")
      ).length;
      setEmbarcacionesIngresadas(embarcacionesHoy);
    } catch (error) {
      console.error("Error al obtener el total de embarcaciones:", error);
    } finally {
      setCargandoEmbarcaciones(false);
    }
  };

  const limpiarFormulario = () => {
    setNuevaVenta({
      cantidad: "0",
      medio_pago_id_2: null,
      monto_medio_pago_1: "",
      monto_medio_pago_2: "",
      diasEstadia: "1",
      extras: [],
    });

    setMedioPago("efectivo");
  };

  const obtenerExtrasSeleccionados = () =>
    Array.isArray(nuevaVenta.extras)
      ? nuevaVenta.extras.map((extra) => ({
          ...extra,
          cantidad: Number(extra.cantidad || 0),
          valor: Number(extra.valor || 0),
        }))
      : [];

  const normalizarCantidad = (valor) => {
    const numero = Number(valor);
    if (!Number.isFinite(numero) || numero < 0) {
      return 0;
    }
    return Math.floor(numero);
  };

  const normalizarDiasEstadia = (valor) => {
    const numero = Number(valor);
    if (!Number.isFinite(numero) || numero < 1) {
      return 1;
    }
    return Math.floor(numero);
  };

  const calcularSubtotalExtras = () =>
    obtenerExtrasSeleccionados().reduce(
      (sum, extra) => sum + Number(extra.valor || 0) * Number(extra.cantidad || 0),
      0
    );

  const calcularTotalBase = (cantidadParam, diasParam) => {
    const cantidad =
      typeof cantidadParam === "number"
        ? cantidadParam
        : normalizarCantidad(nuevaVenta.cantidad || 0);
    const dias =
      typeof diasParam === "number"
        ? diasParam
        : normalizarDiasEstadia(nuevaVenta.diasEstadia || 1);
    const valorEntradaNumero = Number(valorEntrada || 0);
    if (cantidad <= 0 || dias <= 0 || valorEntradaNumero <= 0) return 0;
    return cantidad * valorEntradaNumero * dias;
  };

  const handleAgregarVenta = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const extrasSeleccionados = obtenerExtrasSeleccionados().filter((extra) => extra.cantidad > 0);

    const cantidadNormalizada = normalizarCantidad(nuevaVenta.cantidad);
    const diasNormalizados = normalizarDiasEstadia(nuevaVenta.diasEstadia);

    const cantidadFinal = cantidadNormalizada;
    const diasFinal = diasNormalizados;

    const subtotalExtras = calcularSubtotalExtras();
    const totalBase = calcularTotalBase(cantidadFinal, diasFinal);
    const total = Number((totalBase + subtotalExtras).toFixed(2));

    if (!validarMontos(total)) return;

    const actualizaciones = {};
    if (String(cantidadFinal) !== String(nuevaVenta.cantidad ?? "")) {
      actualizaciones.cantidad = String(cantidadFinal);
    }
    if (String(diasFinal) !== String(nuevaVenta.diasEstadia ?? "")) {
      actualizaciones.diasEstadia = String(diasFinal);
    }
    if (Object.keys(actualizaciones).length > 0) {
      setNuevaVenta((prev) => ({ ...prev, ...actualizaciones }));
    }

    if (!medioPago) {
      alert("Por favor, seleccione un medio de pago.");
      return;
    }

    const montoMedio1 =
      nuevaVenta.medio_pago_id_2 != null
        ? Number(nuevaVenta.monto_medio_pago_1 || 0)
        : total;
    const montoMedio2 =
      nuevaVenta.medio_pago_id_2 != null
        ? Number(nuevaVenta.monto_medio_pago_2 || total - montoMedio1)
        : null;

    const ventaData = {
      cantidad: cantidadFinal,
      valor: total,
      medio_pago_id: medioPago,
      medio_pago_id_2: nuevaVenta.medio_pago_id_2 || null,
      monto_medio_pago_1: montoMedio1,
      monto_medio_pago_2: montoMedio2,
      fecha: format(new Date(), "yyyy-MM-dd"),
      dias_estadia: diasFinal,
      diasEstadia: diasFinal,
      extras: extrasSeleccionados.map((extra) => ({
        id: extra.id,
        nombre: extra.nombre,
        valor: Number(extra.valor),
        cantidad: Number(extra.cantidad),
      })),
      subtotalExtras,
      totalBase,
    };

    setVentaPendiente(ventaData);
    setPassword("");
    setShowPasswordModal(true);
  };
  const validarMontos = (totalCalculado) => {
    const total =
      typeof totalCalculado === "number"
        ? totalCalculado
        : Number((calcularTotalBase() + calcularSubtotalExtras()).toFixed(2));
    const monto1 = Number(nuevaVenta.monto_medio_pago_1 || 0);
    const monto2 = Number(nuevaVenta.monto_medio_pago_2 || 0);

    if (nuevaVenta.medio_pago_id_2 != null) {
      if ((!monto1 && monto1 !== 0) || (!monto2 && monto2 !== 0)) {
        alert("⚠️ Ambos montos son obligatorios cuando se usan dos medios de pago.");
        return false;
      }

      if (monto1 < 0 || monto2 < 0) {
        alert("🚫 Los montos no pueden ser negativos.");
        return false;
      }

      const suma = monto1 + monto2;
      if (Math.abs(suma - total) > 0.01) {
        alert(`⚠️ La suma de los montos (${suma.toFixed(2)}) no coincide con el total (${total.toFixed(2)}).`);
        return false;
      }
    }

    return true;
  };


  const confirmarVentaConPassword = async () => {
    if (!ventaPendiente || enviandoVenta) return;

    const passwordLimpia = password.trim();

    if (!passwordLimpia) {
      alert("La contraseña es obligatoria para registrar la venta.");
      return;
    }

    try {
      setEnviandoVenta(true);
      const response = await registrosApi.verifyPassword({
        password: passwordLimpia,
      });

      if (response.data.esValida && response.data.usuario?.id) {
        const ventaLista = {
          ...ventaPendiente,
          totalVentas: ventaPendiente.valor,
          totalClientes: ventaPendiente.cantidad,
          usuario_id: response.data.usuario.id,
          usuario: response.data.usuario,
          createdAt: new Date().toISOString(),
        };

        setPassword("");
        setShowPasswordModal(false);
        setVentaPendiente(null);
        await handleImprimirEntrada(ventaLista);
      } else {
        const mensaje =
          response.data?.error ||
          "Contraseña incorrecta. No se pudo confirmar la venta.";
        alert(mensaje);
        setPassword("");
      }
    } catch (error) {
      console.error("Error al verificar contraseña:", error);
      const mensaje =
        error.response?.data?.error ||
        "Error al validar la contraseña. Por favor, intente nuevamente.";
      alert(mensaje);
      setPassword("");
    } finally {
      setEnviandoVenta(false);
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      "modoPantallaCompletaRegistros",
      modoPantallaCompleta ? "true" : "false"
    );
  }, [modoPantallaCompleta]);

  useEffect(() => {
    if (!modoPantallaCompleta) {
      setModoPasoAPaso(false);
    }
  }, [modoPantallaCompleta]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "F10") {
        e.preventDefault();
        setModoPantallaCompleta((prev) => !prev);
        return;
      }

      if (e.key === "Enter" && !showPasswordModal && !modalAbierto && !(modoPantallaCompleta && modoPasoAPaso)) {
        e.preventDefault();
        handleAgregarVenta(e);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [modoPantallaCompleta, modoPasoAPaso, showPasswordModal, modalAbierto, handleAgregarVenta]);

  const fetchDesgloseRendicion = async (usuarioId) => {
    const usuarioInfo = usuarios.find((user) => user.id === usuarioId) || null;
    setUsuarioSeleccionadoDesglose(usuarioInfo);
    try {
      const response = await registrosApi.getDesgloseRendicion(usuarioId);
      const detalleNormalizado = (response.data.detalle || []).map((medio) => ({
        ...medio,
        entradas: Number(medio.entradas || 0),
        monto: Number(medio.monto || 0),
        montoEntradas: Number(
          medio.montoEntradas != null
            ? medio.montoEntradas
            : (medio.monto || 0) - (medio.extras || 0)
        ),
        dias: Number(medio.dias || 0),
        dias_detalle: Array.isArray(medio.dias_detalle)
          ? medio.dias_detalle
          : [],
        extras: Number(medio.extras || 0),
        extrasCantidad: Number(medio.extrasCantidad || 0),
        entradasAnuladas: Number(medio.entradasAnuladas || 0),
        montoAnulado: Number(medio.montoAnulado || 0),
        montoEntradasAnulado: Number(
          medio.montoEntradasAnulado != null
            ? medio.montoEntradasAnulado
            : (medio.montoAnulado || 0) - (medio.extrasAnulados || 0)
        ),
        diasAnulados: Number(medio.diasAnulados || 0),
        extrasAnulados: Number(medio.extrasAnulados || 0),
        extrasCantidadAnulados: Number(medio.extrasCantidadAnulados || 0)
      }));

      const totales = response.data.totales || {};
      const montoTotalValor = Number(totales.montoTotal ?? totales.total ?? 0);
      const totalValor = Number(totales.total ?? totales.montoTotal ?? 0);

      const totalesNormalizados = {
        entradas: Number(totales.entradas || 0),
        montoTotal: montoTotalValor,
        total: totalValor,
        montoEntradas: Number(
          totales.montoEntradas ??
            (totales.efectivo?.entradas || totales.otrosMedios?.entradas
              ? (totales.efectivo?.entradas || 0) + (totales.otrosMedios?.entradas || 0)
              : 0)
        ),
        montoExtras: Number(
          totales.montoExtras ??
            (totales.efectivo?.extras || totales.otrosMedios?.extras
              ? (totales.efectivo?.extras || 0) + (totales.otrosMedios?.extras || 0)
              : 0)
        ),
        dias: Number(totales.dias || 0),
        diasAnulados: Number(totales.diasAnulados || 0),
        extras: Number(totales.extras || 0),
        extrasAnulados: Number(totales.extrasAnulados || 0),
        extrasCantidad: Number(totales.extrasCantidad || 0),
        extrasCantidadAnulados: Number(totales.extrasCantidadAnulados || 0),
        entradasAnuladas: Number(totales.entradasAnuladas || 0),
        montoAnulado: Number(totales.montoAnulado || 0),
        montoEntradasAnulados: Number(totales.montoEntradasAnulados || 0),
        montoExtrasAnulados: Number(totales.montoExtrasAnulados || 0),
        efectivo: {
          total: Number(totales.efectivo?.total || 0),
          entradas: Number(totales.efectivo?.entradas || 0),
          extras: Number(totales.efectivo?.extras || 0)
        },
        otrosMedios: {
          total: Number(totales.otrosMedios?.total || 0),
          entradas: Number(totales.otrosMedios?.entradas || 0),
          extras: Number(totales.otrosMedios?.extras || 0)
        }
      };

      const normalizarExtrasDetalle = (lista) =>
        Array.isArray(lista)
          ? lista.map((extra) => ({
              ...extra,
              cantidad: Number(extra.cantidad || 0),
              total: Number(extra.total || 0),
            }))
          : [];

      const extrasDetalle = {
        activos: normalizarExtrasDetalle(response.data.extrasDetalle?.activos),
        anulados: normalizarExtrasDetalle(response.data.extrasDetalle?.anulados),
      };

      const datosFormateados = {
        detalle: detalleNormalizado,
        totales: totalesNormalizados,
        extrasDetalle,
      };
      setVentasUsuarioMedioPago(datosFormateados);
      setModalAbierto(true);
    } catch (error) {
      console.error("Error al obtener el desglose:", error);
      alert(`Error al obtener el desglose: ${error.message}`);
    }
  };

  const handleAnularVenta = async (id) => {
    const confirmar = window.confirm('¿Estás seguro de anular esta venta?');
    if (!confirmar) return;

    const autorizacionPin = window.prompt('Ingrese PIN de autorización de un usuario admin para anular la venta:');
    if (!autorizacionPin || autorizacionPin.toString().trim() === '') {
      alert('La anulación requiere un PIN de autorización válido.');
      return;
    }

    try {
      await registrosApi.annularRegistro(id, autorizacionPin.toString().trim());
      fetchRegistros();
      fetchVentasConMediosDePago();
      alert('Venta anulada correctamente.');
    } catch (error) {
      console.error("Error al anular la venta de entrada:", error);
      alert(error?.response?.data?.error || 'No se pudo anular la venta.');
    }
  };

  const handleImprimirEntrada = async (venta) => {
    if (!venta) return;

    if (venta.anulado) {
      alert('No se puede imprimir una venta anulada.');
      return;
    }

    try {
      let ventaAImprimir = venta;

      if (!venta.id) {
        if (!venta.usuario_id) {
          alert('No se pudo identificar el usuario que registra la venta.');
          return;
        }

        const ventaResponse = await registrosApi.createRegistro({
          fecha: venta.fecha ?? format(new Date(), 'yyyy-MM-dd'),
          totalClientes: venta.totalClientes ?? venta.cantidad,
          totalVentas: venta.totalVentas ?? venta.valor,
          usuario_id: venta.usuario_id,
          idempotencyKey: generarIdempotencyKey(),
          medio_pago_id: venta.medio_pago_id,
          medio_pago_id_2: venta.medio_pago_id_2,
          monto_medio_pago_1: venta.monto_medio_pago_1,
          monto_medio_pago_2: venta.monto_medio_pago_2,
          dias_estadia: venta.dias_estadia ?? venta.diasEstadia,
          extras: (venta.extras || []).map((extra) => ({
            extra_id: extra.id,
            cantidad: extra.cantidad,
          })),
        });

        ventaAImprimir = ventaResponse.data;
        fetchRegistros();
        fetchVentasConMediosDePago();
        limpiarFormulario();
        setFormResetCounter((prev) => prev + 1);
      }

      if (ventaAImprimir?.id && venta?.id) {
        await registrosApi.printRegistro(ventaAImprimir.id);
      }

      setVentaPendiente(null);
    } catch (error) {
      console.error('Error al encolar la impresión o registrar la venta:', error);
      const mensaje =
        error.response?.data?.error ||
        'No se pudo encolar la impresión del ticket.';
      alert(mensaje);
    }
  };

  const exportarExcelDiaActual = () => {
    if (!ventasEntradas || ventasEntradas.length === 0) {
      alert('No hay registros para exportar hoy.');
      return;
    }

    const hoy = new Date();
    const diaFormateado = format(hoy, 'yyyy-MM-dd');

    const diasActivosSet = new Set();
    const diasAnuladosSet = new Set();

    const acumuladores = ventasEntradas.reduce(
      (acc, registro) => {
        const extrasVenta = Array.isArray(registro.extras) ? registro.extras : [];
        const extrasSubtotal = extrasVenta.reduce(
          (sum, extra) => sum + Number(extra.total || 0),
          0
        );
        const extrasCantidad = extrasVenta.reduce(
          (sum, extra) => sum + Number(extra.cantidad || 0),
          0
        );
        const diasLista = Array.isArray(registro.diasDetalle)
          ? registro.diasDetalle
          : Array.from(obtenerDiasVenta(registro));

        const destinoSet = registro.anulado ? diasAnuladosSet : diasActivosSet;
        diasLista.forEach((dia) => destinoSet.add(dia));

        if (registro.anulado) {
          acc.extrasAnulados += extrasSubtotal;
          acc.cantidadExtrasAnulados += extrasCantidad;
        } else {
          acc.extrasActivos += extrasSubtotal;
          acc.cantidadExtrasActivos += extrasCantidad;
        }

        return acc;
      },
      {
        extrasActivos: 0,
        extrasAnulados: 0,
        cantidadExtrasActivos: 0,
        cantidadExtrasAnulados: 0,
      }
    );

    acumuladores.diasActivos = diasActivosSet.size;
    acumuladores.diasAnulados = diasAnuladosSet.size;
    acumuladores.detalleDiasActivos = Array.from(diasActivosSet).sort();
    acumuladores.detalleDiasAnulados = Array.from(diasAnuladosSet).sort();

    const formatCurrency = (value) =>
      `$${Number(value || 0).toLocaleString('es-AR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })}`;

    const resumenFinanciero = totalesFinancieros || {};
    const resumenMedios = desgloseMediosPago || { detalle: [], totales: {} };
    const totalesMedios = resumenMedios.totales || {};

    const header = [
      ['INFORME DIARIO DE REGISTROS', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      ['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      ['Fecha:', format(hoy, 'dd/MM/yyyy'), '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      ['Total de Registros:', ventasEntradas.length, '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      [
        'Clientes activos:',
        totalClientes,
        '',
        '',
        'Clientes anulados:',
        totalClientesAnulados,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Ventas activas:',
        formatCurrency(totalVentas),
        '',
        '',
        'Ventas anuladas:',
        formatCurrency(totalVentasAnuladas),
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Ingresos por entradas (sin extras):',
        formatCurrency(resumenFinanciero.entradas),
        '',
        '',
        'Ingresos por extras:',
        formatCurrency(resumenFinanciero.extras),
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Total general de ingresos:',
        formatCurrency(resumenFinanciero.totalGeneral),
        '',
        '',
        'Cantidad total de extras activos:',
        acumuladores.cantidadExtrasActivos,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Total en efectivo:',
        formatCurrency(totalesMedios.efectivo?.total || 0),
        '',
        '',
        'Entradas en efectivo:',
        formatCurrency(totalesMedios.efectivo?.entradas || 0),
        '',
        '',
        'Extras en efectivo:',
        formatCurrency(totalesMedios.efectivo?.extras || 0),
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Total en otros medios:',
        formatCurrency(totalesMedios.otros?.total || 0),
        '',
        '',
        'Entradas en otros medios:',
        formatCurrency(totalesMedios.otros?.entradas || 0),
        '',
        '',
        'Extras en otros medios:',
        formatCurrency(totalesMedios.otros?.extras || 0),
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Días activos:',
        acumuladores.diasActivos,
        '',
        '',
        'Días anulados:',
        acumuladores.diasAnulados,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Detalle días activos:',
        (acumuladores.detalleDiasActivos || [])
          .map((dia) => format(parseISO(`${dia}T00:00:00`), 'dd/MM/yyyy'))
          .join(', '),
        '',
        '',
        'Detalle días anulados:',
        (acumuladores.detalleDiasAnulados || [])
          .map((dia) => format(parseISO(`${dia}T00:00:00`), 'dd/MM/yyyy'))
          .join(', '),
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Extras activos:',
        formatCurrency(acumuladores.extrasActivos),
        '',
        '',
        'Cantidad extras activos:',
        acumuladores.cantidadExtrasActivos,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      [
        'Extras anulados:',
        formatCurrency(acumuladores.extrasAnulados),
        '',
        '',
        'Cantidad extras anulados:',
        acumuladores.cantidadExtrasAnulados,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
      ['Fecha de Generación:', format(new Date(), 'dd/MM/yyyy HH:mm'), '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      [],
    ];

    const tableHeaders = [
      'Ticket',
      'Fecha Registro',
      'Usuario',
      'Medio Pago 1',
      'Monto 1',
      'Medio Pago 2',
      'Monto 2',
      'Total Ventas',
      'Clientes',
      'Días',
      'Detalle Días',
      'Total Extras',
      'Cantidad Extras',
      'Estado',
      'Fecha Anulación',
      'Monto Anulado',
      'Extras Anulados',
      'Cantidad Extras Anulados',
    ];

    const dataRows = ventasEntradas.map((registro) => {
      const extrasVenta = Array.isArray(registro.extras) ? registro.extras : [];
      const extrasSubtotal = extrasVenta.reduce(
        (sum, extra) => sum + Number(extra.total || 0),
        0
      );
      const extrasCantidad = extrasVenta.reduce(
        (sum, extra) => sum + Number(extra.cantidad || 0),
        0
      );

      const diasDetalleLista = Array.isArray(registro.diasDetalle)
        ? registro.diasDetalle
        : Array.from(obtenerDiasVenta(registro));
      const diasVenta = diasDetalleLista.length || normalizarDiasEstadia(
        registro.diasEstadia ?? registro.dias_estadia ?? 1
      );
      const diasDetalleTexto = diasDetalleLista
        .map((dia) => format(parseISO(`${dia}T00:00:00`), 'dd/MM/yyyy'))
        .join(', ');
      const estado = registro.anulado ? 'Anulada' : 'Activa';
      const fechaAnulacion =
        registro.fechaAnulacion || registro.fecha_anulacion
          ? format(
              new Date(registro.fechaAnulacion || registro.fecha_anulacion),
              'dd/MM/yyyy HH:mm'
            )
          : '-';

      const fechaRegistro = registro.createdAt
        ? format(new Date(registro.createdAt), 'dd/MM/yyyy HH:mm')
        : '--';

      return [
        registro.id ?? registro.ticket ?? '-',
        fechaRegistro,
        registro.usuario?.nombre || `ID: ${registro.usuario_id}`,
        registro.medio_pago?.nombre || '-',
        Number(registro.monto_medio_pago_1 || 0),
        registro.medio_pago_2?.nombre || '-',
        Number(registro.monto_medio_pago_2 || 0),
        Number(registro.totalVentas || 0),
        Number(registro.totalClientes || 0),
        Number(diasVenta || 0),
        diasDetalleTexto || '-',
        Number(extrasSubtotal || 0),
        Number(extrasCantidad || 0),
        estado,
        fechaAnulacion,
        registro.anulado ? Number(registro.totalVentas || 0) : 0,
        registro.anulado ? Number(extrasSubtotal || 0) : 0,
        registro.anulado ? Number(extrasCantidad || 0) : 0,
      ];
    });

    const extrasSection = [];

    const buildExtrasRows = (titulo, lista) => {
      if (!lista || lista.length === 0) return;
      extrasSection.push([
        titulo,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ]);
      extrasSection.push([
        'Nombre',
        'Cantidad',
        'Monto',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ]);
      lista.forEach((extra) => {
        extrasSection.push([
          extra.nombre,
          Number(extra.cantidad || 0),
          Number(extra.total || 0),
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
        ]);
      });
      extrasSection.push([]);
    };

    buildExtrasRows('Detalle de extras activos', resumenExtras.activos);
    buildExtrasRows('Detalle de extras anulados', resumenExtras.anulados);

    const detalleMediosPago = [
      ['DETALLE POR MEDIO DE PAGO', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
      [
        'Medio',
        'Total',
        'Entradas ($)',
        'Extras ($)',
        'Total Anulado',
        'Entradas Anuladas ($)',
        'Extras Anulados ($)',
        'Extras (uds)',
        'Extras anulados (uds)',
        '',
        '',
        '',
        '',
        '',
        '',
        ''
      ],
      ...resumenMedios.detalle.map((medio) => [
        medio.nombre,
        formatCurrency(medio.total || medio.monto || 0),
        formatCurrency(medio.entradasMonto || 0),
        formatCurrency(medio.extrasMonto || medio.extras || 0),
        formatCurrency(medio.totalAnulado || medio.montoAnulado || 0),
        formatCurrency(medio.entradasMontoAnulado || 0),
        formatCurrency(medio.extrasMontoAnulado || medio.extrasAnulados || 0),
        Math.round(medio.extrasCantidad || 0),
        Math.round(medio.extrasCantidadAnulados || 0),
        '',
        '',
        '',
        '',
        '',
        '',
        ''
      ]),
      []
    ];

    const sheetData = [
      ...header,
      ...detalleMediosPago,
      tableHeaders,
      ...dataRows,
      ...extrasSection
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    worksheet['!cols'] = [
      { wch: 12 },
      { wch: 18 },
      { wch: 22 },
      { wch: 16 },
      { wch: 12 },
      { wch: 16 },
      { wch: 12 },
      { wch: 14 },
      { wch: 10 },
      { wch: 10 },
      { wch: 22 },
      { wch: 14 },
      { wch: 14 },
      { wch: 12 },
      { wch: 18 },
      { wch: 14 },
      { wch: 16 },
      { wch: 16 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Registros Hoy');

    XLSX.writeFile(workbook, `registros_${diaFormateado}.xlsx`);
  };

  const ventasPorUsuario = usuarios.map((usuario) => {
    const ventasUsuario = ventasEntradas.filter(
      (venta) => venta.usuario_id === usuario.id
    );

    const ventasActivas = ventasUsuario.filter((venta) => !venta.anulado);
    const ventasAnuladas = ventasUsuario.filter((venta) => venta.anulado);

    const totalVentasUsuario = ventasActivas.reduce(
      (sum, venta) => sum + obtenerMontoVenta(venta),
      0
    );
    const totalClientesUsuario = ventasActivas.reduce(
      (sum, venta) => sum + obtenerTotalClientesVenta(venta),
      0
    );

    const diasActivosUsuario = new Set();
    ventasActivas.forEach((venta) => {
      const dias = Array.isArray(venta.diasDetalle)
        ? venta.diasDetalle
        : Array.from(obtenerDiasVenta(venta));
      dias.forEach((dia) => diasActivosUsuario.add(dia));
    });
    const totalDiasUsuario = diasActivosUsuario.size;

    const totalExtrasUsuario = ventasActivas.reduce((sum, venta) => {
      const extrasVenta = Array.isArray(venta.extras) ? venta.extras : [];
      return (
        sum +
        extrasVenta.reduce((acum, extra) => acum + Number(extra.total || 0), 0)
      );
    }, 0);

    const totalExtrasCantidadUsuario = ventasActivas.reduce((sum, venta) => {
      const extrasVenta = Array.isArray(venta.extras) ? venta.extras : [];
      return (
        sum +
        extrasVenta.reduce((acum, extra) => acum + Number(extra.cantidad || 0), 0)
      );
    }, 0);

    const totalVentasUsuarioAnuladas = ventasAnuladas.reduce(
      (sum, venta) => sum + obtenerMontoVenta(venta),
      0
    );
    const totalClientesUsuarioAnulados = ventasAnuladas.reduce(
      (sum, venta) => sum + obtenerTotalClientesVenta(venta),
      0
    );

    const diasAnuladosUsuario = new Set();
    ventasAnuladas.forEach((venta) => {
      const dias = Array.isArray(venta.diasDetalle)
        ? venta.diasDetalle
        : Array.from(obtenerDiasVenta(venta));
      dias.forEach((dia) => diasAnuladosUsuario.add(dia));
    });
    const totalDiasUsuarioAnulados = diasAnuladosUsuario.size;

    const totalExtrasUsuarioAnulados = ventasAnuladas.reduce((sum, venta) => {
      const extrasVenta = Array.isArray(venta.extras) ? venta.extras : [];
      return (
        sum +
        extrasVenta.reduce((acum, extra) => acum + Number(extra.total || 0), 0)
      );
    }, 0);

    const totalExtrasCantidadUsuarioAnulados = ventasAnuladas.reduce(
      (sum, venta) => {
        const extrasVenta = Array.isArray(venta.extras) ? venta.extras : [];
        return (
          sum +
          extrasVenta.reduce((acum, extra) => acum + Number(extra.cantidad || 0), 0)
        );
      },
      0
    );

    const ventaConMedioPago = ventasConMediosDePago.find(
      (v) =>
        v.usuario_id === usuario.id && v.fecha === format(fecha, "yyyy-MM-dd")
    );

    return {
      id: usuario.id,
      nombre: usuario.nombre,
      totalVentas: totalVentasUsuario,
      totalClientes: totalClientesUsuario,
      totalDias: totalDiasUsuario,
      totalExtras: totalExtrasUsuario,
      totalExtrasCantidad: totalExtrasCantidadUsuario,
      totalVentasAnuladas: totalVentasUsuarioAnuladas,
      totalClientesAnulados: totalClientesUsuarioAnulados,
      totalDiasAnulados: totalDiasUsuarioAnulados,
      totalExtrasAnulados: totalExtrasUsuarioAnulados,
      totalExtrasCantidadAnulados: totalExtrasCantidadUsuarioAnulados,
      ventasAnuladas,
      fecha: format(fecha, "yyyy-MM-dd"),
      medioPago: ventaConMedioPago
        ? ventaConMedioPago.MediosDePago.nombre
        : "No especificado",
    };
  });

  const ventasFiltradas = (
    filtroUsuario
      ? ventasEntradas.filter((venta) => venta.usuario_id === filtroUsuario)
      : ventasEntradas
  ).map((venta) => {
    const medioPagoEncontrado = mediosDePago.find(
      (medio) => medio.id === venta.medio_pago_id
    );
    const ventaConMedioPago = ventasConMediosDePago.find(
      (v) => v.id === venta.id
    );

    return {
      ...venta,
      medioPago: medioPagoEncontrado?.nombre || "No especificado",
      medioPago2: venta.medio_pago_2?.nombre || null, // <- Nuevo campo para el segundo medio
    };
  });

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-azul-oscuro to-azul-lago text-blanco-puro p-4 md:p-8 ${
        showPasswordModal || modalAbierto ? "pointer-events-none" : ""
      }`}
    >
      <motion.header
        className="mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl md:text-4xl font-bold text-amarillo-dorado">
          Registro de Ingresos
        </h1>
        <p className="text-xl text-gris-piedra">
          {format(new Date(), "EEEE d 'de' MMMM 'de' yyyy", { locale: es })}
        </p>
      </motion.header>

      <div className="flex flex-col gap-6">
        <div className="order-1">
          {" "}
          {/* Formulario siempre arriba */}
          <FormularioVenta
            nuevaVenta={nuevaVenta}
            setNuevaVenta={setNuevaVenta}
            medioPago={medioPago}
            setMedioPago={setMedioPago}
            mediosDePago={mediosDePago}
            extrasDisponibles={extras}
            valorEntrada={valorEntrada}
            modoPantallaCompleta={modoPantallaCompleta}
            setModoPantallaCompleta={setModoPantallaCompleta}
            modoPasoAPaso={modoPasoAPaso}
            setModoPasoAPaso={setModoPasoAPaso}
            handleAgregarVenta={handleAgregarVenta}
            limpiarFormulario={limpiarFormulario}
            resetStepTrigger={formResetCounter}
          />
        </div>
        <div className="order-2 lg:order-3">
          {" "}
          {/* Lista siempre abajo */}
          <ListaVentas
            ventasFiltradas={ventasFiltradas}
            handleImprimirEntrada={handleImprimirEntrada} // Nombre corregido
            handleAnularVenta={handleAnularVenta}
          />
        </div>
      </div>

      <div className="mt-6 grid gap-6">
        <EmbarcacionesSection
          embarcacionesIngresadas={embarcacionesIngresadas}
          cargandoEmbarcaciones={cargandoEmbarcaciones}
          onObtenerCantidad={handleObtenerEmbarcaciones}
        />

        <ResumenDia
          totalClientes={totalClientes}
          embarcacionesIngresadas={embarcacionesIngresadas}
          totalVentas={totalVentas}
          totalClientesAnulados={totalClientesAnulados}
          totalVentasAnuladas={totalVentasAnuladas}
          ventasPorUsuario={ventasPorUsuario}
          extrasResumen={resumenExtras}
          diasResumen={resumenDiasUnicos}
          totalesFinancieros={totalesFinancieros}
          desgloseMediosPago={desgloseMediosPago}
          onDesglose={fetchDesgloseRendicion}
          onExportExcel={exportarExcelDiaActual}
        />
      </div>
      <ModalPassword
        showPasswordModal={showPasswordModal}
        ventaPendiente={ventaPendiente}
        password={password}
        setPassword={setPassword}
        enviandoVenta={enviandoVenta}
        confirmarVentaConPassword={confirmarVentaConPassword}
        onCancel={() => {
          if (enviandoVenta) return;
          setShowPasswordModal(false);
          setPassword("");
          setVentaPendiente(null);
        }}
      />
      <VentasDesgloseModal
        show={modalAbierto}
        ventas={ventasUsuarioMedioPago}
        usuario={usuarioSeleccionadoDesglose}
        onClose={() => setModalAbierto(false)}
      />
    </div>
  );
};

export default RegistroIngresos;
